import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import CustomAlert from "../../components/ui/CustomAlert";
import SearchForm from "../../components/ui/SearchForm";
import PriceFilters from "../../components/ui/PriceRanges";
// import UserInfoModal from "../../components/modals/UserInfoModal";
import NumberCard from "../../components/ui/NumberCard";
import LoadMoreButton from "../../components/ui/LoadMoreButton";
import { fetchNumberListings } from "../../api";
import styles from "./style.module.css";
import bannerGif from "../../assets/images/simsim_at_store.gif";
import SEOHeading from '../../components/ui/SEOHeading';
import { generateImageAltText } from '../../utils/contentOptimization';

const LIMIT = 15;

const Numbers = () => {
  // State
  const [prefix, setPrefix] = useState("");
  const [digits, setDigits] = useState(Array(7).fill(""));
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // const [showUserInfoModal, setShowUserInfoModal] = useState(false);
  const [offset, setOffset] = useState(0);
  const [numbers, setNumbers] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [currentPriceRange, setCurrentPriceRange] = useState({
    minPrice: null,
    maxPrice: null,
  });

  // Refs
  const inputRefs = useRef([]);
  const priceRangeRef = useRef(null);
  const navigate = useNavigate();

  // Utility to show alerts
  const handleShowAlert = (message, type) => {
    setAlert({ message, type });
  };

  // Build filters from current state
  const getCurrentSearchFilters = () => {
    const filters = {};
    if (prefix) filters.prefix = prefix;
    if (digits.some((d) => d)) {
      digits.forEach((digit, index) => {
        if (digit) {
          filters[`num${index + 1}`] = digit;
        }
      });
    }
    return filters;
  };

  // Fetch filtered data from API
  const loadFilteredNumbers = async (currentOffset, searchFilters, priceRange) => {
    try {
      if (currentOffset === 0) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const params = new URLSearchParams();
      params.set("offset", currentOffset);
      params.set("limit", LIMIT);

      // Add search filters
      Object.entries(searchFilters).forEach(([key, value]) => {
        params.set(key, value);
      });

      // Add price range filter
      const { minPrice, maxPrice } = priceRange;
      if (minPrice !== null) params.set("minPrice", minPrice);
      if (maxPrice !== null) params.set("maxPrice", maxPrice);

      const numberListings = await fetchNumberListings(params);
      if (currentOffset === 0) {
        setNumbers(numberListings);
      } else {
        setNumbers((prev) => [...prev, ...numberListings]);
      }

      setOffset(currentOffset + numberListings.length);
      setHasMore(numberListings.length === LIMIT);
    } catch (error) {
      console.error(error);
      handleShowAlert("Məlumatları yükləyərkən xəta baş verdi", "error");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Initial data load
  useEffect(() => {
    const defaultPriceRange = { minPrice: null, maxPrice: null };
    setLoading(true);
    loadFilteredNumbers(0, {}, defaultPriceRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers
  const handlePriceRangeChange = (range) => {
    setLoading(true);
    setCurrentPriceRange(range);
    setNumbers([]);
    setOffset(0);

    const searchFilters = getCurrentSearchFilters();
    loadFilteredNumbers(0, searchFilters, range);
  };

  const handleSearch = () => {
    const hasDigits = digits.some((d) => d);
    if (!prefix && !hasDigits && !searchTriggered) {
      return;
    }

    setLoading(true);
    const resetPriceRange = { minPrice: null, maxPrice: null };
    setCurrentPriceRange(resetPriceRange);

    if (priceRangeRef.current?.resetPriceRange) {
      priceRangeRef.current.resetPriceRange();
    }

    setSearchTriggered(hasDigits || !!prefix);
    setNumbers([]);
    setOffset(0);

    const searchFilters = getCurrentSearchFilters();
    loadFilteredNumbers(0, searchFilters, resetPriceRange);
  };

  const handleLoadMore = async () => {
    setLoading(true);
    const searchFilters = getCurrentSearchFilters();
    loadFilteredNumbers(offset, searchFilters, currentPriceRange);
  };

  const handleInputChange = (index, value) => {
    if (value.length <= 1 && /^[0-9]*$/.test(value)) {
      const newDigits = [...digits];
      newDigits[index] = value;
      setDigits(newDigits);

      if (value.length === 1 && index < 6) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Enter" && inputRefs.current[index]) {
      inputRefs.current[index].focus();
    }
  };

  const handleContactClick = (numberListing) => {
    // Note: data is passed via router state. Order page persists to sessionStorage to survive refresh.
  // Navigate to order page via router state (no query params)
  navigate("/order", { state: { numberListing } });
  };

  // User info submission moved to the Order page

  return (
    <>
      {alert && (
        <CustomAlert
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert(null)}
        />
      )}

      <div className={styles.contentSection}>
        <SEOHeading level={1} className={styles.headerText}>Axtarış</SEOHeading>

        <SearchForm
          prefix={prefix}
          digits={digits}
          inputRefs={inputRefs}
          handleSearch={handleSearch}
          handlePrefixChange={(e) => setPrefix(e.target.value)}
          handleDigitChange={handleInputChange}
          handleKeyDown={handleKeyDown}
          setSearchResults={setNumbers}
          setDigits={setDigits}
          setPrefix={setPrefix}
        />

        <Link to="/catalog" style={{ display: 'none' }}>
          <img src={bannerGif} alt={generateImageAltText('numbers', 'Banner')} className={styles.bannerImage} />
        </Link>

        <PriceFilters ref={priceRangeRef} onPriceRangeChange={handlePriceRangeChange} />

        {loading && numbers.length === 0 && (
          <LoadMoreButton onClick={() => {}} loading={true} />
        )}

        {!loading && numbers.length === 0 ? (
          <div className="text-center my-5">
            <p>Nəticə tapılmadı</p>
          </div>
        ) : (
          <>
            {numbers.map((numberListing) => (
              <NumberCard
                key={numberListing.msisdn}
                numberListing={numberListing}
                handleContactClick={handleContactClick}
              />
            ))}

            {loadingMore && <LoadMoreButton onClick={() => {}} loading={true} />}

            {hasMore && !loading && !loadingMore && (
              <LoadMoreButton onClick={handleLoadMore} loading={false} />
            )}
          </>
        )}
      </div>

  {/* UserInfoModal replaced by dedicated Order page */}
    </>
  );
};

export default Numbers;
